import NiceModal from "@ebay/nice-modal-react";
import Dummy from "components/Dummy";
import P from "components/P";
import Container from "layouts/container/Container";
import { BrowserDetectContext } from "providers/BrowserEventProvider";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AuthReducer } from "shared/redux/reducers/auth/AuthReducer";
import { OrderInfoReducer } from "shared/redux/reducers/shopping/OrderInfoReducer";
import { removeLocalStorage } from "shared/utils/Utils";
import { AuthContext } from "providers/AuthProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { requester } from "App";
import biometricService from "shared/BiometricService";
import { toast } from "react-toastify";

function Setting() {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isMobile } = useContext(BrowserDetectContext);
    const { userName } = useContext(AuthContext);
    const [biometricStatus, setBiometricStatus] = useState({
        supported: false,
        enabled: false,
        loading: true
    });

    useEffect(() => {
        // 로컬에서만 생체인증 지원 여부 확인
        const isSupported = biometricService.isSupported();

        // 로컬 스토리지에서 생체인증 상태 확인 (임시 방편)
        const storedStatus = localStorage.getItem('puffu_biometric_supported');
        const isEnabled = storedStatus === 'true';

        setBiometricStatus({
            supported: isSupported,
            enabled: isEnabled,
            loading: false
        });
    }, []);

    // 생체인증 등록 시작 함수
    const startBiometricRegistration = async () => {
        if (!biometricStatus.supported) return;

        // 🔥 미등록 상태에서만 호출되므로 바로 등록 확인 모달 표시
        try {
            const hasRegisteredKey = await biometricService.hasRegisteredKey();
            if (hasRegisteredKey) {
                // 키가 있지만 상태가 enabled=false로 설정된 경우
                // 상태만 변경
                setBiometricStatus(prev => ({
                    ...prev,
                    enabled: true
                }));
                localStorage.setItem('puffu_biometric_supported', 'true');
                toast.info("생체인증이 이미 등록되어 있습니다.", {
                    autoClose: 2000, position: toast.POSITION.BOTTOM_CENTER
                });
                return;
            }

            // 미등록 상태면 등록 확인
            NiceModal.show("biometricConfirm", {
                title: "생체인증 등록",
                content: "생체인증을 등록하면 지문이나 얼굴 인식으로 간편하게 로그인할 수 있습니다. 등록하시겠습니까?",
                confirmText: "등록하기",
                cancelText: "취소",
                onConfirm: registerBiometric
            });
        } catch (error) {
            console.error("생체인증 키 확인 오류:", error);
            // 오류 발생 시 기본적으로 등록 모달 표시
            NiceModal.show("biometricConfirm", {
                title: "생체인증 등록",
                content: "생체인증을 등록하면 지문이나 얼굴 인식으로 간편하게 로그인할 수 있습니다. 등록하시겠습니까?",
                confirmText: "등록하기",
                cancelText: "취소",
                onConfirm: registerBiometric
            });
        }
    };

    // 생체인증 등록 함수
    const registerBiometric = async () => {
        setBiometricStatus(prev => ({ ...prev, loading: true }));
        
        try {
            // 최대 시도 횟수와 현재 시도 횟수 설정
            const maxAttempts = 5;
            let currentAttempt = 0;
            let registrationSuccess = false;
            
            const attemptRegistration = async () => {
                try {
                    currentAttempt++;
                    
                    // 사용자 ID로 휴대폰 번호 사용
                    const userId = userName;
                    
                    // 생체인증 키 등록 진행
                    const biometricKeyResult = await biometricService.registerBiometricKey(userId);
                    
                    if (biometricKeyResult && biometricKeyResult.publicKey) {
                        // 등록 성공, 서버에 생체인증 정보 업데이트
                        const updateData = {
                            userName: userName,
                            biometricPublicKey: biometricKeyResult.publicKey,
                            biometricAlgorithm: biometricKeyResult.algorithm || "EC",
                            biometricEnabled: true,
                            biometricKeySignature: calculateKeySignature(biometricKeyResult.publicKey),
                            biometricPlatform: biometricService.getPlatform()
                        };
                        
                        // 여기서만 API 호출
                        requester.checkMemberBiometricStatus(updateData, (result) => {
                            if (result.code === 0) {
                                registrationSuccess = true;
                                setBiometricStatus(prev => ({
                                    ...prev,
                                    enabled: true,
                                    loading: false
                                }));
                                
                                // 로컬 스토리지에 상태 저장
                                localStorage.setItem('puffu_biometric_supported', 'true');
                                
                                toast.success("생체인증이 성공적으로 등록되었습니다!", {
                                    autoClose: 2000, position: toast.POSITION.BOTTOM_CENTER
                                });
                            } else {
                                setBiometricStatus(prev => ({ ...prev, loading: false }));
                                throw new Error("서버 업데이트 실패");
                            }
                        });
                    } else {
                        throw new Error("생체인증 키 생성 실패");
                    }
                } catch (error) {
                    console.error("생체인증 등록 시도 오류:", error);
                    
                    if (currentAttempt < maxAttempts) {
                        // 실패 확인 모달
                        NiceModal.show("biometricConfirm", {
                            title: "생체인증 등록 실패",
                            content: `생체인증 등록에 실패했습니다. 다시 시도하시겠습니까? (${currentAttempt}/${maxAttempts})`,
                            confirmText: "다시 시도",
                            cancelText: "취소",
                            onConfirm: attemptRegistration,
                            onCancel: () => {
                                setBiometricStatus(prev => ({ ...prev, loading: false }));
                            }
                        });
                    } else {
                        setBiometricStatus(prev => ({ ...prev, loading: false }));
                        toast.error("생체인증 등록에 여러 번 실패했습니다. 나중에 다시 시도해주세요.", {
                            autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
                        });
                    }
                }
            };
            
            // 첫 번째 시도 시작
            await attemptRegistration();
            
        } catch (error) {
            setBiometricStatus(prev => ({ ...prev, loading: false }));
            toast.error("생체인증 등록 중 오류가 발생했습니다.", {
                autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
            });
        }
    };
    
    // 공개키 해시 계산 함수
    const calculateKeySignature = (publicKeyBase64) => {
        try {
            return publicKeyBase64.substring(0, 64);
        } catch (error) {
            console.error("키 시그니처 생성 오류:", error);
            return publicKeyBase64;
        }
    };

    const settingItems = [
        {
            title: "이용 설정",
            items: [
                {
                    label: "알림 설정",
                    icon: fas.faBell,
                    onClick: () => navigate(`/ws/${id}/mobile/settings/notifications`, {replace: true})
                },
            ]
        },
        {
            title: "고객 지원",
            items: [
                { label: "이용약관", icon: fas.faFileAlt, onClick: () => navigate(`/ws/${id}/mobile/settings/policy`, {replace: true}) },
                { label: "개인정보 처리방침", icon: fas.faUserShield, onClick: () => navigate(`/ws/${id}/mobile/settings/privacy`, {replace: true}) },
            ]
        },
        ...(isMobile && 
            !/iPad|iPhone|iPod/.test(navigator.userAgent) && 
            biometricStatus.supported && 
            !biometricStatus.enabled && 
            !biometricStatus.loading ? [{
            title: "보안",
            items: [
                {
                    label: "생체인증 등록",
                    icon: fas.faFingerprint,
                    onClick: startBiometricRegistration,
                    description: "지문이나 얼굴 인식으로 간편 로그인"
                }
            ]
        }] : [])
    ];

    return (
        <div style={{ height: "100%", backgroundColor: "#fff" }}>
            <Dummy height={"56px"} />
            <div 
                style={{
                    padding: "24px 20px",
                    borderBottom: "1px solid #f5f5f5",
                    backgroundColor: "#fff",
                    cursor: "pointer"
                }}
                onClick={() => navigate(`/ws/${id}/mobile/settings/profile`, {replace: true})}
            >
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    marginBottom: "16px"
                }}>
                    <div style={{
                        width: "64px",
                        height: "64px",
                        borderRadius: "50%",
                        backgroundColor: "var(--main-color)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                        <FontAwesomeIcon
                            icon={fas.faUser}
                            style={{
                                fontSize: "24px",
                                color: "#fff"
                            }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <P size={22} weight={700} style={{ marginBottom: "4px" }}>
                            {userName} 님
                        </P>
                        <P size={14} color="#666">
                            오늘도 즐거운 하루 되세요!
                        </P>
                    </div>
                    <FontAwesomeIcon
                        icon={fas.faChevronRight}
                        style={{
                            color: "#999",
                            fontSize: "14px"
                        }}
                    />
                </div>
            </div>
            <div style={{ padding: "20px" }}>
                {settingItems.map((section, index) => (
                    <div key={index} style={{ marginBottom: "32px" }}>
                        <P size={14} color={"#666"} weight={600} style={{ marginBottom: "12px" }}>
                            {section.title}
                        </P>
                        <div style={{
                            backgroundColor: "#fff",
                            borderRadius: "12px",
                            overflow: "hidden",
                            border: "1px solid #f5f5f5"
                        }}>
                            {section.items.map((item, itemIndex) => (
                                <div
                                    key={itemIndex}
                                    onClick={item.onClick}
                                    style={{
                                        padding: "16px",
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        borderBottom: itemIndex < section.items.length - 1 ? "1px solid #f5f5f5" : "none",
                                        backgroundColor: "#fff"
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={item.icon}
                                        style={{
                                            width: "20px",
                                            color: item.color || "#333",
                                            marginRight: "12px"
                                        }}
                                    />
                                    <P
                                        size={15}
                                        weight={500}
                                        color={item.color || "#333"}
                                    >
                                        {item.label}
                                    </P>
                                    <FontAwesomeIcon
                                        icon={fas.faChevronRight}
                                        style={{
                                            marginLeft: "auto",
                                            color: "#999",
                                            fontSize: "14px"
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            {/* <P
                size={13}
                color={"#999"}
                style={{
                    textAlign: "center",
                    padding: "16px",
                    position: "fixed",
                    bottom: "80px",
                    left: 0,
                    right: 0
                }}
            >
                버전 v1.0.3
            </P> */}
            {/* <Dummy height={"80px"} /> */}
        </div>
    );
}

export default Setting;